import { gql } from './client';
import { emit } from './events';
import { CONFIG } from '../config';
import { runEventForResults } from '../pageUtils';

export interface PlanningUser {
  userId: string;
  email: string;
  status: string;
}

export async function listUsers(): Promise<PlanningUser[]> {
  const data = await gql<{ listUsers: PlanningUser[] | null }>(
    `query ListPlanningUsers($appId: String!) {
      listUsers(applicationId: $appId) { userId email status }
    }`,
    { appId: CONFIG.applicationId },
  );
  return data.listUsers ?? [];
}

export interface UserIdentity {
  email?: string;
  name?: string;
}

// Best effort: the email list and the name directory are looked up independently, so a person's email still shows
// when the name lookup is refused (it is permission checked) and the other way round.
export async function resolveUserIdentities(userIds: string[]): Promise<Map<string, UserIdentity>> {
  const [users, directory] = await Promise.all([
    listUsers().catch(() => [] as PlanningUser[]),
    listUserDirectory().catch(() => null),
  ]);
  const out = new Map<string, UserIdentity>();
  for (const id of userIds) {
    const email = users.find(u => u.userId === id)?.email;
    const name = directory?.names.get(id);
    if (email || name) out.set(id, { email, name });
  }
  return out;
}

export const NOT_PERMITTED = "You don't have permission to do that.";

// The workflow checks the caller's permission itself and, when it is missing, completes with the later steps skipped,
// so a missing step result is how a refusal shows up.
export interface UserDirectory {
  balances: Map<string, number>;
  names: Map<string, string>;
  profiles: Map<string, { forename: string; surname: string }>;
  // Absent means no stored value, which behaves as Mock.
  aiModes: Map<string, AiMode>;
  // Who is an administrator, as recorded by the Users page toggle (the role itself decides access).
  administrators: Set<string>;
  // The Terms version each person last accepted, and when. Absent means they haven't accepted any.
  termsAccepted: Map<string, { version: string; at: string }>;
}

/**
 * How a person's model calls run. Mock: canned output. Live: real, paid Bedrock calls. Dev: an assessment stops before the
 * model and shows what it would have sent, for pasting into a Claude chat, then takes the reply pasted back; every other
 * model call is mocked. Stored as mockMode (true for Mock and Dev) plus devMode.
 */
export type AiMode = 'Mock' | 'Live' | 'Dev';

export function aiModeOf(p: { mockMode?: boolean | null; devMode?: boolean | null }): AiMode {
  return p.devMode === true ? 'Dev' : p.mockMode === false ? 'Live' : 'Mock';
}

// Foundations holds only an email per user; the first/last name lives in Planning's own profile service.
export async function listUserDirectory(): Promise<UserDirectory> {
  const results = await runEventForResults(connId => emit('ListTokenWallets', crypto.randomUUID(), {}, connId));
  const wallets = results['wallets'];
  if (!Array.isArray(wallets)) throw new Error(NOT_PERMITTED);
  const profiles = Array.isArray(results['profiles']) ? results['profiles'] as { id: string; forename?: string | null; surname?: string | null; mockMode?: boolean | null; devMode?: boolean | null; administrator?: boolean | null; termsAcceptedVersion?: string | null; termsAcceptedAt?: string | null }[] : [];
  const names = new Map<string, string>();
  const profileNames = new Map<string, { forename: string; surname: string }>();
  const aiModes = new Map<string, AiMode>();
  const administrators = new Set<string>();
  const termsAccepted = new Map<string, { version: string; at: string }>();
  for (const p of profiles) {
    if (p.termsAcceptedVersion != null && p.termsAcceptedAt != null) termsAccepted.set(p.id, { version: p.termsAcceptedVersion, at: p.termsAcceptedAt });
    profileNames.set(p.id, { forename: p.forename ?? '', surname: p.surname ?? '' });
    if (typeof p.mockMode === 'boolean' || p.devMode === true) aiModes.set(p.id, aiModeOf(p));
    if (p.administrator === true) administrators.add(p.id);
    const full = [p.forename, p.surname].filter(Boolean).join(' ');
    if (full) names.set(p.id, full);
  }
  return {
    balances: new Map((wallets as { id: string; balance: number }[]).map(w => [w.id, w.balance])),
    names,
    profiles: profileNames,
    aiModes,
    administrators,
    termsAccepted,
  };
}

export async function adjustTokens(userId: string, delta: number): Promise<void> {
  const results = await runEventForResults(connId => emit('AdjustTokens', crypto.randomUUID(), { userId, delta }, connId));
  if (results['adjust'] == null) throw new Error(NOT_PERMITTED);
}

export async function setUserAiMode(userId: string, mode: AiMode): Promise<void> {
  const payload = { userId, mockMode: mode !== 'Live', devMode: mode === 'Dev' };
  const results = await runEventForResults(connId => emit('SetPlanningUserMockMode', crypto.randomUUID(), payload, connId));
  if (results['setMockMode'] == null) throw new Error(NOT_PERMITTED);
}

// Assigns or removes the Planning Administrator (and Policy Input Manager) roles. The workflow refuses anyone who isn't an
// administrator, and anyone changing themselves.
export async function setUserAdministrator(userId: string, administrator: boolean): Promise<void> {
  await runEventForResults(connId => emit('SetPlanningAdministrator', crypto.randomUUID(), { userId, administrator }, connId), 60_000);
}

// emailSent is false only when nothing could be sent: an existing user is emailed through SES, so this means that failed.
export async function inviteUser(email: string, firstName: string, lastName: string, mockMode: boolean): Promise<{ emailSent: boolean }> {
  const results = await runEventForResults(
    connId => emit('InvitePlanningUser', crypto.randomUUID(), { email, firstName, lastName, mockMode }, connId),
    60_000,
  );
  const invite = results['invite'] as { data?: { emailSent?: boolean } } | null | undefined;
  if (invite == null) throw new Error(NOT_PERMITTED);
  return { emailSent: invite.data?.emailSent !== false };
}

// Removes the person from Planning (they can no longer sign in to it) and takes away all their Planning roles.
// Their token balance and any cases they created are left as they are.
export async function revokeUser(userId: string): Promise<void> {
  const results = await runEventForResults(
    connId => emit('RevokePlanningUser', crypto.randomUUID(), { userId }, connId),
    60_000,
  );
  if (results['removeFromApplication'] == null) throw new Error(NOT_PERMITTED);
}
